import { CumulativeBooster } from "../cumulative_booster.js";
import { CumulativeBoosterID } from "../cumulative_booster_id.js";

export interface StickyMineSettings
{
  fuse_seconds: number;
  push_radius: number;
  push_speed: number;
}

export interface StickyMinesBoosterContext
{
  /** Picks up to `count` visible blocks spread over the level. Returns how many mines were aimed. */
  prepareStickyMineTargets( count: number ): number;
  /** Launches the mine aimed at target `index`; false when that block is already gone. */
  launchStickyMine( index: number, settings: StickyMineSettings ): boolean;
  /** Mines that are still flying, blinking or exploding. */
  getActiveStickyMineCount(): number;
  clearStickyMines(): void;
}

/**
 * Passive level-start booster: a few sticky mines fly onto blocks, blink for
 * a moment and blow. The blast pushes blocks away but never breaks them.
 */
export class StickyMinesBooster extends CumulativeBooster<StickyMinesBoosterContext>
{
  public static readonly DEFAULT_MINE_COUNT = 3;
  public static readonly DEFAULT_SHOT_INTERVAL_SECONDS = 0.15;
  public static readonly DEFAULT_FUSE_SECONDS = 1.2;
  public static readonly DEFAULT_PUSH_RADIUS = 2.6;
  public static readonly DEFAULT_PUSH_SPEED = 7.5;

  private readonly mine_count: number;
  private readonly shot_interval_seconds: number;
  private readonly settings: StickyMineSettings;
  private target_count = 0;
  private next_index = 0;
  private time_to_next_launch = 0;

  public constructor(
    mine_count = StickyMinesBooster.DEFAULT_MINE_COUNT,
    shot_interval_seconds = StickyMinesBooster.DEFAULT_SHOT_INTERVAL_SECONDS,
    fuse_seconds = StickyMinesBooster.DEFAULT_FUSE_SECONDS,
    push_radius = StickyMinesBooster.DEFAULT_PUSH_RADIUS,
    push_speed = StickyMinesBooster.DEFAULT_PUSH_SPEED
  )
  {
    super( CumulativeBoosterID.Mine );
    this.mine_count = Math.max( 1, Math.floor( mine_count ) );
    this.shot_interval_seconds = Math.max( 0, shot_interval_seconds );
    this.settings = {
      fuse_seconds: Math.max( 0.1, fuse_seconds ),
      push_radius: Math.max( 0.5, push_radius ),
      push_speed: Math.max( 0, push_speed ),
    };
  }

  protected override onActivate( context: StickyMinesBoosterContext ): void
  {
    this.target_count = context.prepareStickyMineTargets( this.mine_count );
    this.next_index = 0;
    this.time_to_next_launch = 0;
    if ( this.target_count === 0 )
      this.complete();
    else
      this.launchNext( context );
  }

  protected override onUpdate( context: StickyMinesBoosterContext, dt: number ): void
  {
    this.time_to_next_launch -= dt;
    while ( this.next_index < this.target_count && this.time_to_next_launch <= 0 )
      this.launchNext( context );

    if ( this.next_index >= this.target_count && context.getActiveStickyMineCount() === 0 )
      this.complete();
  }

  protected override onDeactivate( context: StickyMinesBoosterContext, cancelled: boolean ): void
  {
    if ( cancelled )
      context.clearStickyMines();
  }

  protected override onReset(): void
  {
    this.target_count = 0;
    this.next_index = 0;
    this.time_to_next_launch = 0;
  }

  private launchNext( context: StickyMinesBoosterContext ): void
  {
    context.launchStickyMine( this.next_index, this.settings );
    this.next_index++;
    this.time_to_next_launch += this.shot_interval_seconds;
  }
}
